import React, { useState, useRef, useEffect } from 'react';
import vendorApi from '../services/api';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { isPasswordStrong, getPasswordChecks } from '../utils/passwordValidation';
import { PasswordStrengthIndicator } from '../components/ui/PasswordStrengthIndicator';

type Step = 'email' | 'code' | 'reset' | 'success';

interface ForgotPasswordProps {
  onBackToLogin: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBackToLogin }) => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'code' && resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    } else if (resendCountdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [step, resendCountdown]);

  const extractErrorMessage = (err: any, defaultMessage: string): string => {
    if (err.response?.data?.message) {
      return err.response.data.message;
    } else if (err.response?.data?.error) {
      return err.response.data.error;
    } else if (err.response?.status === 422) {
      const validationErrors = err.response.data.errors;
      if (validationErrors) {
        const firstError = Object.values(validationErrors)[0];
        return Array.isArray(firstError) ? firstError[0] : String(firstError);
      }
    } else if (err.response?.status === 429) {
      return 'Too many attempts. Please wait a moment and try again.';
    } else if (err.response?.status >= 500) {
      return 'Server error. Please try again later.';
    } else if (err.message) {
      return err.message;
    } else if (!navigator.onLine) {
      return 'No internet connection. Please check your network.';
    }
    return defaultMessage;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await vendorApi.forgotPassword(email);
      setSuccessMessage(result.message || 'Reset code sent to your email');
      setStep('code');
      setResendCountdown(60);
      setCanResend(false);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to send reset code. Please check your email and try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleResendCode = async () => {
    setError('');
    setIsLoading(true);

    try {
      await vendorApi.forgotPassword(email);
      setSuccessMessage('A new code has been sent to your email.');
      setResendCountdown(60);
      setCanResend(false);
      setCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to resend code. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setIsLoading(true);

    try {
      await vendorApi.verifyResetCode(email, fullCode);
      setSuccessMessage('');
      setStep('reset');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Invalid or expired code. Please check and try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPasswordStrong(password)) {
      const missing = getPasswordChecks(password).filter(c => !c.met).map(c => c.label);
      setError(`Password is missing: ${missing.join(', ')}`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const fullCode = code.join('');
      const result = await vendorApi.resetPassword(email, fullCode, password, confirmPassword);
      setSuccessMessage(result.message || 'Password reset successful!');
      setStep('success');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to reset password. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'email': return 'Reset Password';
      case 'code': return 'Verify Code';
      case 'reset': return 'New Password';
      case 'success': return 'Success!';
      default: return 'Reset Password';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'email': return 'Enter your email to receive a reset code';
      case 'code': return `Enter the 6-digit code sent to ${email}`;
      case 'reset': return 'Create a new password for your account';
      case 'success': return 'Your password has been reset successfully';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a1628]">
      {/* Left Side - Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-[55%] relative items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2240] to-[#0a1628]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />

        <div className="relative z-10 max-w-lg px-16">
          <img
            src={process.env.PUBLIC_URL + '/spotts-logo.svg'}
            alt="Spotts"
            className="h-14 mb-12"
          />

          <h1 className="text-4xl font-bold text-white mb-4 leading-tight tracking-tight">
            Reset Your Password
          </h1>

          <p className="text-lg text-slate-400 mb-10 leading-relaxed">
            Don't worry, we'll help you get back into your account securely.
          </p>

          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${step === 'email' || step === 'code' || step === 'reset' || step === 'success' ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                <span className="text-blue-400 font-semibold">1</span>
              </div>
              <span className={`text-sm ${step === 'email' ? 'text-white font-medium' : 'text-slate-400'}`}>Enter your email address</span>
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${step === 'code' || step === 'reset' || step === 'success' ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                <span className="text-blue-400 font-semibold">2</span>
              </div>
              <span className={`text-sm ${step === 'code' ? 'text-white font-medium' : 'text-slate-400'}`}>Verify with 6-digit code</span>
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${step === 'reset' || step === 'success' ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                <span className="text-blue-400 font-semibold">3</span>
              </div>
              <span className={`text-sm ${step === 'reset' ? 'text-white font-medium' : 'text-slate-400'}`}>Create new password</span>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/5">
            <p className="text-slate-500 text-sm">
              Find your <span className="text-blue-400">spot</span>. Play your <span className="text-blue-400">sport</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center bg-white lg:rounded-l-[2rem] px-6 sm:px-12">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-10">
            <img
              src={process.env.PUBLIC_URL + '/spotts-logo-dark.svg'}
              alt="Spotts"
              className="h-10"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{getStepTitle()}</h2>
            <p className="mt-1.5 text-sm text-gray-500">{getStepDescription()}</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-100 rounded-lg p-3.5 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}

          {successMessage && step !== 'success' && (
            <div className="mb-6 bg-green-50 border border-green-100 rounded-lg p-3.5 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  className="block w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
                  placeholder="you@venue.com"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 text-sm font-semibold rounded-lg text-white bg-[#007AFF] hover:bg-[#0066DD] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending code...
                  </span>
                ) : (
                  'Send Reset Code'
                )}
              </button>

              <button
                type="button"
                onClick={onBackToLogin}
                className="w-full flex justify-center items-center py-2.5 px-4 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </button>
            </form>
          )}

          {/* Step 2: Code Verification */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Enter verification code
                </label>
                <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { codeInputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className={`w-11 h-12 text-center text-xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all ${
                        error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="text-center">
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Resend Code
                  </button>
                ) : (
                  <p className="text-sm text-gray-500">
                    Resend code in <span className="font-semibold text-blue-600">{resendCountdown}s</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || code.join('').length !== 6}
                className="w-full flex justify-center py-2.5 px-4 text-sm font-semibold rounded-lg text-white bg-[#007AFF] hover:bg-[#0066DD] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode(['', '', '', '', '', '']);
                  setError('');
                  setSuccessMessage('');
                }}
                className="w-full flex justify-center items-center py-2.5 px-4 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Change Email
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="block w-full px-3.5 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                <PasswordStrengthIndicator password={password} />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    className="block w-full px-3.5 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 text-sm font-semibold rounded-lg text-white bg-[#007AFF] hover:bg-[#0066DD] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          {/* Success State */}
          {step === 'success' && (
            <div className="space-y-5">
              <div className="bg-green-50 border border-green-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-green-900 mb-1">
                  Password Reset Complete
                </h3>
                <p className="text-sm text-green-700">
                  You can now sign in with your new password.
                </p>
              </div>

              <button
                type="button"
                onClick={onBackToLogin}
                className="w-full flex justify-center py-2.5 px-4 text-sm font-semibold rounded-lg text-white bg-[#007AFF] hover:bg-[#0066DD] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Continue to Login
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Need help?{' '}
              <a href="mailto:info@spottsapp.com" className="text-blue-600 hover:text-blue-700 font-medium">
                Contact support
              </a>
            </p>
          </div>

          <p className="text-center text-[11px] text-gray-300 mt-6">
            &copy; 2026 SPOTTS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
